/**
 * The `load_type` vocabulary, which is a Postgres ENUM and therefore closed.
 *
 * Read from production 2026-09-04:
 *
 *   float, vacuum, water, fluid_haul, hot_oiler, picker, dump, gravel,
 *   crane, personnel, lowbed, rental_delivery, rental_pickup
 *
 * `POST /trucking/loads` inserts `loadType` straight into that column, so a
 * value outside the enum is not a validation message — it is a 22P02 and a 500.
 *
 * NewLoadBooking used to offer eighteen labels and a "+ Add Custom Load Type"
 * button on top of them. Nine of the eighteen have no enum value at all
 * (Hiboy, Winch Tractor, Bed Truck, Tandem, Combo, Steamer, Acid, Frac, Rig
 * Move), and a custom type is a string the database cannot store under any
 * spelling. None of that was discoverable from the screen because the screen
 * never submitted anything.
 *
 * `rental_delivery` and `rental_pickup` are deliberately NOT offered here.
 * They are set by the API when a rental dispatch creates its own load; a
 * dispatcher booking a haul by hand is not doing that.
 */

/** Every label the `load_type` enum accepts. */
export const LOAD_TYPE_VALUES = [
  'float',
  'vacuum',
  'water',
  'fluid_haul',
  'hot_oiler',
  'picker',
  'dump',
  'gravel',
  'crane',
  'personnel',
  'lowbed',
  'rental_delivery',
  'rental_pickup',
] as const;

export type LoadType = (typeof LOAD_TYPE_VALUES)[number];

/**
 * The types a dispatcher may book, in the order the booking screen shows them.
 *
 * `desc` is the operator-facing sentence that used to live inline in
 * NewLoadBooking; it is here so the label and the enum value cannot drift
 * apart in two files.
 */
export const LOAD_TYPE_OPTIONS: readonly {
  value: LoadType;
  label: string;
  desc: string;
}[] = [
  { value: 'float', label: 'Float', desc: 'Flatbed equipment hauling' },
  { value: 'lowbed', label: 'Lowbed', desc: 'Heavy/oversize equipment (lowboy)' },
  { value: 'vacuum', label: 'Vacuum', desc: 'Liquid waste removal' },
  { value: 'water', label: 'Water', desc: 'Fresh/produced water haul' },
  { value: 'fluid_haul', label: 'Fluid Haul', desc: 'General fluid transport' },
  { value: 'hot_oiler', label: 'Hot Oiler', desc: 'Heated oil treatment' },
  { value: 'picker', label: 'Picker', desc: 'Crane/picker truck' },
  { value: 'crane', label: 'Crane', desc: 'Crane lift work' },
  { value: 'dump', label: 'Dump', desc: 'Gravel/dirt dump loads' },
  { value: 'gravel', label: 'Gravel', desc: 'Aggregate hauling' },
  { value: 'personnel', label: 'Personnel', desc: 'Crew transport' },
];

/** Labels for rendering a stored value, including the two the API assigns. */
const LOAD_TYPE_LABELS: Record<LoadType, string> = {
  float: 'Float',
  vacuum: 'Vacuum',
  water: 'Water',
  fluid_haul: 'Fluid Haul',
  hot_oiler: 'Hot Oiler',
  picker: 'Picker',
  dump: 'Dump',
  gravel: 'Gravel',
  crane: 'Crane',
  personnel: 'Personnel',
  lowbed: 'Lowbed',
  rental_delivery: 'Rental Delivery',
  rental_pickup: 'Rental Pickup',
};

/**
 * Exhaustive, per CLAUDE.md: no binary check, no `replace(/_/g, ' ')` fallback
 * that echoes a raw value and turns a display bug into a reported data bug. An
 * unrecognised value renders as the absent marker.
 */
export function getLoadTypeLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return LOAD_TYPE_LABELS[value as LoadType] ?? '—';
}

export function isLoadType(value: string | null | undefined): value is LoadType {
  return !!value && (LOAD_TYPE_VALUES as readonly string[]).includes(value);
}
